/**
 * F-0578 (`docs/concept-waiting-motion.md`, WAIT-2 to WAIT-6, WAIT-13): the ten waiting studies that
 * replace the FEATit mark and every spinner as the sign that something is loading. Jay, 2026-10-16:
 * "I like them all - we can randomize them"; "everywhere we have waiting icons we should have that
 * randomized spinners".
 *
 * Each study's geometry, timing and colours are defined once, here, and drawn from here. They are
 * ported from the prototype (`docs/brand/motion/waiting-motion-studies.html`) number for number; the
 * one change is colour on the light theme, which takes each spectrum stop's light twin
 * (`colour-twins.ts`) so a thin line clears 3:1 on the light canvas (WAIT-13).
 *
 * The drawing talks to a small slice of the 2D canvas API (`IWaitingCanvas`), so the web's canvas
 * draws it and a test can record it. Strings and pure functions only (`@featit/shared` is
 * platform-agnostic).
 */
import {
  BRAND_SPECTRUMS,
  contrastRatio,
  DARK_CANVAS,
  hexToOklch,
  LIGHT_CANVAS,
  maxSrgbChroma,
  oklchToHex,
} from './colour-twins.js';

/** A canvas gradient, as far as a study uses one. */
export interface IWaitingGradient {
  addColorStop(offset: number, colour: string): void;
}

/** The slice of `CanvasRenderingContext2D` a study draws with. */
export interface IWaitingCanvas {
  globalAlpha: number;
  globalCompositeOperation: string;
  lineWidth: number;
  lineCap: string;
  lineJoin: string;
  strokeStyle: string | object;
  fillStyle: string | object;
  beginPath(): void;
  closePath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  arc(x: number, y: number, radius: number, start: number, end: number): void;
  roundRect(x: number, y: number, w: number, h: number, radius: number): void;
  stroke(): void;
  fill(): void;
  createLinearGradient(x0: number, y0: number, x1: number, y1: number): IWaitingGradient;
}

/** One frame of one study. */
export interface IWaitingFrame {
  /** The study's centre, in canvas units. */
  cx: number;
  cy: number;
  /** The study's unit: its hexagon's radius. */
  R: number;
  /** Seconds since the session's waits began. */
  t: number;
  /** 0 while moving, 1 at the rest shape (the settle, and Reduce Motion). */
  k: number;
  /** The whole drawing's opacity. */
  alpha: number;
  /** The page theme. Without a `ground`, the theme's palette as it is (dark: originals; light: twins). */
  dark: boolean;
  /**
   * WAIT-13 (I-1689): the colour the study is drawn on, `#RRGGBB` — the page, a panel, a card or a
   * busy button's fill, as the screen composites it. Given, it decides the palette instead of the
   * theme: every brand colour is drawn as its tone that clears 3:1 on this ground (`waitingToneOn`).
   */
  ground?: string;
  /** The thinnest stroke this size may draw (WAIT-6). */
  minW: number;
  /** In-app sizes keep scattered parts closer in. */
  compact: boolean;
}

export const WAITING_STUDY_IDS = [
  'convergence',
  'relay',
  'tide',
  'lattice',
  'strands',
  'counterpoint',
  'gathering',
  'assemble',
  'connect',
  'align',
] as const;

export type WaitingStudyId = (typeof WAITING_STUDY_IDS)[number];

export interface IWaitingStudy {
  id: WaitingStudyId;
  name: string;
  /**
   * How far the drawing reaches from its centre, in units of `R`, measured over two cycles (the
   * prototype's own figures, raised where a frame reached further: Tide 1.6 → 1.62, Assemble's
   * fly-in 1.8 → 2.05). A small box fits this.
   */
  ext: number;
  draw(ctx: IWaitingCanvas, frame: IWaitingFrame): void;
}

export type WaitingSizeType = 'launch' | 'section' | 'inline';

/** The named numbers around the studies (each a setting or a named constant, not a decision). */
export const WAITING_MOTION = Object.freeze({
  /** WAIT-6: the box side, px, of the two in-app sizes. */
  sectionPx: 40,
  inlinePx: 16,
  /**
   * WAIT-6: at launch the unit `R` is this share of the window's short side, at most `launchUnitMaxPx`,
   * centred on it: the prototype's size (50.7 pt at 390 × 844), not the old mark's 56 pt box.
   */
  launchUnit: 0.13,
  launchUnitMaxPx: 80,
  /**
   * WAIT-6: the launch canvas's side in units of `R`. At launch the studies are not compact, and
   * Convergence scatters to 2.7 `R` plus a dot, so the canvas holds 3 `R` each way.
   */
  launchBoxUnits: 6,
  /** WAIT-6: the thinnest stroke, px, per size. */
  minStroke: { launch: 1.3, section: 1.6, inline: 1.3 } as Readonly<
    Record<WaitingSizeType, number>
  >,
  /** WAIT-8: captions after these many ms (launch and section only). */
  captionsMs: [2000, 5000, 10000] as readonly number[],
  /** WAIT-9: the settle to the rest shape, then the fade. */
  settleMs: 300,
  fadeMs: 200,
  /** WAIT-10: Reduce Motion is the rest shape breathing between these opacities on this cycle. */
  reduceMotion: { minAlpha: 0.55, maxAlpha: 1, periodS: 2.4 },
  /**
   * WAIT-13 (D-0313 c): a fill at least this many px across, as drawn, is a large fill. On a light
   * ground it keeps the bright original, outlined in its deeper tone; a smaller one is a small mark
   * and takes the deeper tone itself, as strokes do.
   */
  largeFillPx: 8,
  /**
   * WAIT-13 (T-0695): Tide's rings fade as they spread, and the fade is counted. At full strength a
   * ring is drawn in its tone that clears this ratio on its ground, so there is room for a fade; it
   * never fades under `tideFloorRatio`, the 3:1 a graphic needs with a little room for 8-bit pixels.
   */
  tideFullRatio: 4.5,
  tideFloorRatio: 3.1,
});

/** The brand palettes the studies draw from (`docs/brand/README.md`), as the prototype lists them. */
export const WAITING_PALETTE = Object.freeze({
  solar: ['#FFE397', '#FFBD53', '#F28A2D'] as readonly string[],
  ring: ['#C8DF69', '#99CF5C', '#5DB779', '#31A99E'] as readonly string[],
  frame: ['#31A99E', '#23B5C7', '#288EE0', '#3D67DE'] as readonly string[],
});

const LIGHT_TWIN: ReadonlyMap<string, string> = new Map(
  BRAND_SPECTRUMS.flatMap((spectrum) =>
    spectrum.stops.map((stop) => [stop.original.toUpperCase(), stop.twin] as const),
  ),
);

/**
 * WAIT-13: a palette colour as a theme draws it. Dark draws the original; light draws the stop's
 * light twin, the deeper tone that clears 3:1 on the light canvas. A colour outside the spectra is
 * drawn as given.
 */
export function waitingTone(colour: string, dark: boolean): string {
  return dark ? colour : (LIGHT_TWIN.get(colour.toUpperCase()) ?? colour);
}

/** WAIT-13: a ground is dark when its OKLCH lightness is under half, as `toneOnCanvas` reads it. */
export function waitingGroundIsDark(ground: string): boolean {
  return hexToOklch(ground).l < 0.5;
}

/** Each colour's tone per ground, worked out once: `toneOnCanvas` is a search, a frame is not. */
const TONE_ON: Map<string, string> = new Map();
const TONE_ON_LIMIT = 1024;

/**
 * A tone moved to clear more than 3:1, so the blends a study draws between two such tones (Tide's
 * ring, Connect's links) do not round back under it. A tone that already clears is not moved.
 */
const MOVED_TONE_RATIO = 3.1;

/**
 * The tone of `hex` at the nearest lightness that clears `minimum`:1 on `ground`, lighter or darker,
 * keeping its OKLCH hue and as much chroma as sRGB holds. `toneOnCanvas` only moves away from the
 * canvas; on a mid ground (a red danger fill) the nearer side can be the other one, and a pale tone
 * reads better there than a near-black one.
 */
function nearestToneClearing(hex: string, ground: string, minimum: number): string {
  const { l, c, h } = hexToOklch(hex);
  const at = (lightness: number) =>
    oklchToHex(lightness, Math.min(c, maxSrgbChroma(lightness, h)), h);
  const towards = (end: number): { tone: string; step: number } | null => {
    if (contrastRatio(at(end), ground) < minimum) return null;
    let near = l;
    let far = end;
    for (let i = 0; i < 24; i++) {
      const mid = (near + far) / 2;
      if (contrastRatio(at(mid), ground) >= minimum) far = mid;
      else near = mid;
    }
    return { tone: at(far), step: Math.abs(far - l) };
  };
  const options = [towards(1), towards(0)].filter((o): o is { tone: string; step: number } => !!o);
  options.sort((a, b) => a.step - b.step);
  return options[0]?.tone ?? hex;
}

/**
 * WAIT-13 (I-1689): a palette colour as it is drawn on `ground`. The theme's tone of it first (the
 * original on a dark ground, the light twin on a light one); if that does not clear 3:1 on this
 * ground, the nearest lightness that clears it with a little room (`MOVED_TONE_RATIO`), same hue.
 * On the two page canvases this is `waitingTone` exactly; on a busy button's dark fill in the light
 * theme it is the original's bright tone, on the light #F1F2F2 panel a slightly deeper twin.
 */
export function waitingToneOn(colour: string, ground: string): string {
  const key = `${ground}|${colour}`;
  const known = TONE_ON.get(key);
  if (known) return known;
  const start = waitingTone(colour, waitingGroundIsDark(ground));
  const tone =
    contrastRatio(start, ground) >= 3
      ? start
      : nearestToneClearing(start, ground, MOVED_TONE_RATIO);
  if (TONE_ON.size >= TONE_ON_LIMIT) TONE_ON.clear();
  TONE_ON.set(key, tone);
  return tone;
}

const TAU = Math.PI * 2;
const clamp = (v: number, a = 0, b = 1) => Math.min(b, Math.max(a, v));
const lerp = (a: number, b: number, k: number) => a + (b - a) * k;
/**
 * `Math.pow`, rounded to six decimals. V8's `pow` is not correctly rounded and has changed between
 * Node versions: under Node v24.22.0 `Math.pow(0.5, 1.5)` is `0.3535533905932738`, where the
 * fixture's recording had `0.35355339059327373`, so the committed frames (`waiting-shell.guard`) were
 * one runtime's bytes. Rounded here, in the motion itself, every runtime draws and records the same
 * numbers. `sqrt` is correctly rounded by IEEE 754 and needs none.
 */
const pow = (base: number, exponent: number) => Math.round(Math.pow(base, exponent) * 1e6) / 1e6;
const easeIO = (x: number) => (x < 0.5 ? 4 * x * x * x : 1 - pow(-2 * x + 2, 3) / 2);
const easeOut = (x: number) => 1 - pow(1 - x, 3);
const rgb = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
const mixHex = (a: string, b: string, k: number) => {
  const A = rgb(a);
  const B = rgb(b);
  return `rgb(${A.map((v, i) => Math.round(lerp(v, B[i], k))).join(',')})`;
};
const along = (list: readonly string[], q: number) => {
  const x = clamp(q) * (list.length - 1);
  const i = Math.min(list.length - 2, Math.floor(x));
  return mixHex(list[i], list[i + 1], x - i);
};
/** The prototype's integer hash: a fixed scatter per cycle, the same on every device. */
const rand = (s: number) => {
  let t = (s * 2654435761) >>> 0;
  t = Math.imul(t ^ (t >>> 15), 1 | t);
  t ^= t + Math.imul(t ^ (t >>> 7), 61 | t);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};
type PointType = [number, number];
const hex = (cx: number, cy: number, r: number, rot = -Math.PI / 2): PointType[] =>
  Array.from({ length: 6 }, (_, i) => [
    cx + r * Math.cos(rot + (i * Math.PI) / 3),
    cy + r * Math.sin(rot + (i * Math.PI) / 3),
  ]);
const poly = (ctx: IWaitingCanvas, pts: PointType[]) => {
  ctx.beginPath();
  pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
  ctx.closePath();
};
/** The palette's side: the ground's when the frame names one, else the page theme's. */
const onDark = (s: IWaitingFrame) => (s.ground ? waitingGroundIsDark(s.ground) : s.dark);
const toned = (c: string, s: IWaitingFrame) =>
  s.ground ? waitingToneOn(c, s.ground) : waitingTone(c, s.dark);
const ink = (s: IWaitingFrame, a: number) =>
  onDark(s) ? `rgba(242,242,240,${a})` : `rgba(26,29,31,${a})`;
const lw = (s: IWaitingFrame, f: number) => Math.max(s.R * f, s.minW);
const tones = (list: readonly string[], s: IWaitingFrame) => list.map((c) => toned(c, s));
/** The three palettes' one stop each, as the prototype picks them per theme, before toning. */
const trioOriginals = (s: IWaitingFrame) =>
  onDark(s) ? ['#FFBD53', '#99CF5C', '#288EE0'] : ['#F28A2D', '#5DB779', '#3D67DE'];
const trio = (s: IWaitingFrame) => tones(trioOriginals(s), s);
/**
 * WAIT-13 (D-0313 c, Jay 2026-10-16): fills the path just built in brand colour `original`, `extent`
 * px across at its narrowest. On a light ground a large fill keeps the prototype's bright original
 * and gets a thin outline in its deeper tone, which clears 3:1 on the ground; anything else (a
 * small mark, any fill on a dark ground) is filled in its tone, as before.
 */
const fillMark = (ctx: IWaitingCanvas, s: IWaitingFrame, original: string, extent: number) => {
  if (onDark(s) || extent < WAITING_MOTION.largeFillPx) {
    ctx.fillStyle = toned(original, s);
    ctx.fill();
    return;
  }
  ctx.fillStyle = original;
  ctx.fill();
  const width = ctx.lineWidth;
  ctx.strokeStyle = toned(original, s);
  ctx.lineWidth = s.minW;
  ctx.stroke();
  ctx.lineWidth = width;
};
const hexOf = (colour: string) =>
  `#${(colour.match(/\d+/g) ?? [])
    .slice(0, 3)
    .map((v) => Number(v).toString(16).padStart(2, '0'))
    .join('')}`;
/** `colour` at `alpha` over `ground`, both `#RRGGBB`, as the screen composites it. */
const over = (colour: string, alpha: number, ground: string) => {
  const g = rgb(ground);
  return hexOf(`rgb(${rgb(colour).map((v, i) => Math.round(g[i] + (v - g[i]) * alpha))})`);
};

interface ITideInk {
  ring: string[];
  /** The least opacity a ring is drawn at: every colour it passes through clears the floor. */
  floor: number;
}
const TIDE_INK: Map<string, ITideInk> = new Map();

/**
 * WAIT-13 (T-0695): Tide's ring tones on this frame's ground, and its fade floor, worked out once per
 * ground. Each tone clears `tideFullRatio` (a tone that already does is kept); the floor is the least
 * opacity at which each of 33 colours along the ring, laid over the ground, clears `tideFloorRatio`.
 */
function tideInk(s: IWaitingFrame): ITideInk {
  const ground = s.ground ?? (s.dark ? DARK_CANVAS : LIGHT_CANVAS);
  const known = TIDE_INK.get(ground);
  if (known) return known;
  const { tideFullRatio, tideFloorRatio } = WAITING_MOTION;
  const ring = WAITING_PALETTE.ring.map((colour) => {
    const tone = toned(colour, s);
    return contrastRatio(tone, ground) >= tideFullRatio
      ? tone
      : nearestToneClearing(tone, ground, tideFullRatio);
  });
  let floor = 0;
  for (let i = 0; i <= 32; i++) {
    const colour = hexOf(along(ring, i / 32));
    let low = 0;
    let high = 1;
    if (contrastRatio(over(colour, 1, ground), ground) < tideFloorRatio) low = high;
    for (let step = 0; step < 20 && high - low > 1e-4; step++) {
      const mid = (low + high) / 2;
      if (contrastRatio(over(colour, mid, ground), ground) >= tideFloorRatio) high = mid;
      else low = mid;
    }
    floor = Math.max(floor, high);
  }
  const ink = { ring, floor };
  if (TIDE_INK.size >= TONE_ON_LIMIT) TIDE_INK.clear();
  TIDE_INK.set(ground, ink);
  return ink;
}

const onHex = (V: PointType[], x: number): PointType => {
  const i = Math.floor(x);
  const fr = x - i;
  const a = V[((i % 6) + 6) % 6];
  const b = V[(((i + 1) % 6) + 6) % 6];
  return [lerp(a[0], b[0], fr), lerp(a[1], b[1], fr)];
};

/** WAIT-2: the ten studies. Periods and phase splits are in seconds and cycle shares. */
export const WAITING_STUDIES: readonly IWaitingStudy[] = Object.freeze([
  {
    id: 'convergence',
    name: 'Convergence',
    ext: 1.85,
    draw(ctx, s) {
      const { cx, cy, R, t, k } = s;
      const C = 3.4;
      const ci = Math.floor(t / C);
      const p = (t % C) / C;
      const originals = ['#3D67DE', '#288EE0', '#23B5C7', '#31A99E', '#5DB779', '#99CF5C'];
      const cols = tones(originals, s);
      const scatter = (c: number, i: number): PointType => {
        const a =
          (c * Math.PI) / 3 +
          (i * Math.PI) / 3 -
          Math.PI / 2 +
          (rand(c * 31 + i * 7 + 3) - 0.5) * 1.3;
        const r =
          R *
          (s.compact
            ? 1.45 + rand(c * 17 + i * 13 + 5) * 0.35
            : 1.9 + rand(c * 17 + i * 13 + 5) * 0.8);
        return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
      };
      const rot0 = (ci * Math.PI) / 3 - Math.PI / 2;
      let rot = rot0;
      if (p >= 0.45 && p < 0.72) rot = rot0 + (Math.PI / 3) * easeIO((p - 0.45) / 0.27);
      else if (p >= 0.72) rot = rot0 + Math.PI / 3;
      const V = hex(cx, cy, R, rot);
      const pts = V.map((v, i): PointType => {
        let q: PointType;
        if (p < 0.45) {
          const a = scatter(ci, i);
          const e = easeIO(p / 0.45);
          q = [lerp(a[0], v[0], e), lerp(a[1], v[1], e)];
        } else if (p < 0.72) q = v;
        else {
          const b = scatter(ci + 1, i);
          const e = easeIO((p - 0.72) / 0.28);
          q = [lerp(v[0], b[0], e), lerp(v[1], b[1], e)];
        }
        return [lerp(q[0], v[0], k), lerp(q[1], v[1], k)];
      });
      let e = 0;
      if (p > 0.38 && p < 0.5) e = (p - 0.38) / 0.12;
      else if (p >= 0.5 && p < 0.72) e = 1;
      else if (p >= 0.72 && p < 0.82) e = 1 - (p - 0.72) / 0.1;
      e = Math.max(easeIO(clamp(e)), k);
      ctx.lineWidth = lw(s, 0.07);
      ctx.lineCap = 'round';
      for (let i = 0; i < 6; i++) {
        const a = pts[i];
        const b = pts[(i + 1) % 6];
        const g = ctx.createLinearGradient(a[0], a[1], b[0], b[1]);
        g.addColorStop(0, cols[i]);
        g.addColorStop(1, cols[(i + 1) % 6]);
        ctx.globalAlpha = s.alpha * e * 0.9;
        ctx.strokeStyle = g;
        ctx.beginPath();
        ctx.moveTo(a[0], a[1]);
        ctx.lineTo(b[0], b[1]);
        ctx.stroke();
      }
      pts.forEach((q, i) => {
        const d = Math.hypot(q[0] - V[i][0], q[1] - V[i][1]);
        ctx.globalAlpha = s.alpha * (0.3 + 0.7 * clamp(1 - d / (R * 1.6)));
        const radius = Math.max(R * 0.13, s.minW * 1.1);
        ctx.beginPath();
        ctx.arc(q[0], q[1], radius, 0, TAU);
        fillMark(ctx, s, originals[i], radius * 2);
      });
    },
  },
  {
    id: 'relay',
    name: 'Relay',
    ext: 1.2,
    draw(ctx, s) {
      const { cx, cy, R, t, k } = s;
      const VO = hex(cx, cy, R * 1.05);
      const VI = hex(cx, cy, R * 0.5, -Math.PI / 2 + Math.PI / 6);
      const blueOriginals = ['#23B5C7', '#288EE0', '#3D67DE', '#31A99E', '#288EE0', '#3D67DE'];
      const blues = tones(blueOriginals, s);
      const greens = tones(['#99CF5C', '#5DB779', '#31A99E'], s);
      const seg = (
        V: PointType[],
        u: number,
        len: number,
        colour: (x: number) => string,
        width: number,
        alpha: number,
        n = 22,
      ) => {
        ctx.lineWidth = width;
        for (let j = 0; j < n; j++) {
          const q0 = u - len + (len * j) / n;
          const q1 = u - len + (len * (j + 1)) / n;
          const A = onHex(V, q0);
          const B = onHex(V, q1);
          ctx.globalAlpha = alpha * pow((j + 1) / n, 1.5);
          ctx.strokeStyle = colour(q1);
          ctx.beginPath();
          ctx.moveTo(A[0], A[1]);
          ctx.lineTo(B[0], B[1]);
          ctx.stroke();
        }
      };
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      // The two tracks.
      ctx.globalAlpha = s.alpha;
      ctx.lineWidth = lw(s, 0.06);
      ctx.strokeStyle = ink(s, 0.1);
      poly(ctx, VO);
      ctx.stroke();
      ctx.lineWidth = lw(s, 0.045);
      ctx.strokeStyle = ink(s, 0.08);
      poly(ctx, VI);
      ctx.stroke();
      // Outer team: one side per step, a new blue for each side.
      const step = 0.52;
      const move = 0.36;
      const n = Math.floor(t / step);
      const f = t % step;
      const u = n + easeIO(Math.min(1, f / move));
      // Its painted trail: each side it ran keeps its colour and fades.
      for (let q = 1; q <= 6; q++) {
        const e = n - q;
        const age = t - (e * step + move);
        if (age < 0) continue;
        const A = VO[((e % 6) + 6) % 6];
        const B = VO[(((e + 1) % 6) + 6) % 6];
        ctx.globalAlpha = s.alpha * (1 - k) * 0.55 * Math.exp(-age * 1.1);
        ctx.strokeStyle = blues[((e % 6) + 6) % 6];
        ctx.lineWidth = lw(s, 0.07);
        ctx.beginPath();
        ctx.moveTo(A[0], A[1]);
        ctx.lineTo(B[0], B[1]);
        ctx.stroke();
      }
      // Inner team: the same way round as the outer one (both hexagons list their corners in the same
      // turn), one side per one and a half steps; its trail fades behind its head, as the outer one's.
      const istep = step * 1.5;
      const imove = 0.5;
      const m = Math.floor(t / istep);
      const g = t % istep;
      const v = m + easeIO(Math.min(1, g / imove));
      seg(
        VI,
        v + 0.7,
        0.7,
        (x) => greens[((Math.floor(x) % 3) + 3) % 3],
        lw(s, 0.075),
        s.alpha * (1 - k),
      );
      // Batons: at each corner the outer runner reached, a dot runs the spoke inwards.
      for (let q = 0; q < 3; q++) {
        const e = n - q;
        const age = t - (e * step + move);
        if (age < 0 || age > 0.6) continue;
        const vi = (((e + 1) % 6) + 6) % 6;
        const O = VO[vi];
        const I = VI[vi];
        const w = easeIO(clamp(age / 0.45));
        ctx.globalAlpha = s.alpha * (1 - k) * 0.35 * (1 - w);
        ctx.strokeStyle = blues[vi];
        ctx.lineWidth = lw(s, 0.03);
        ctx.beginPath();
        ctx.moveTo(O[0], O[1]);
        ctx.lineTo(lerp(O[0], I[0], w), lerp(O[1], I[1], w));
        ctx.stroke();
        ctx.globalAlpha = s.alpha * (1 - k) * (1 - clamp((age - 0.45) / 0.15));
        const radius = Math.max(R * 0.07, s.minW);
        ctx.beginPath();
        ctx.arc(lerp(O[0], I[0], w), lerp(O[1], I[1], w), radius, 0, TAU);
        fillMark(ctx, s, blueOriginals[vi], radius * 2);
      }
      // The outer runner itself, on top: its colour is the side it is on.
      seg(
        VO,
        u,
        0.9,
        (x) => blues[((Math.floor(x) % 6) + 6) % 6],
        lw(s, 0.1),
        s.alpha * (1 - k),
        26,
      );
      // Settled: both frames drawn whole.
      if (k > 0) {
        const gO = ctx.createLinearGradient(cx - R, cy - R, cx + R, cy + R);
        tones(WAITING_PALETTE.frame, s).forEach((c, i) => gO.addColorStop(i / 3, c));
        ctx.globalAlpha = s.alpha * k;
        ctx.strokeStyle = gO;
        ctx.lineWidth = lw(s, 0.09);
        poly(ctx, VO);
        ctx.stroke();
        ctx.strokeStyle = toned('#5DB779', s);
        ctx.lineWidth = lw(s, 0.06);
        poly(ctx, VI);
        ctx.stroke();
      }
    },
  },
  {
    id: 'tide',
    name: 'Tide',
    ext: 1.62,
    draw(ctx, s) {
      const { cx, cy, R, t, k } = s;
      // T-0695: the fade is counted, so a ring fades from its full tone down to the floor, never past.
      const { ring, floor } = tideInk(s);
      const T = 2.7;
      const gap = 0.9;
      for (let j = 0; j < 3; j++) {
        const raw = t - j * gap;
        if (raw < 0) continue;
        const q = (raw % T) / T;
        const r = R * (0.3 + 1.3 * easeOut(q));
        const fade = pow(1 - q, 1.4) * clamp(q / 0.08);
        ctx.globalAlpha = s.alpha * (1 - k) * Math.max(floor, fade);
        ctx.strokeStyle = along(ring, q);
        ctx.lineWidth = lw(s, 0.11 * (1 - q) + 0.025);
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, TAU);
        ctx.stroke();
      }
      if (k > 0) {
        ctx.globalAlpha = s.alpha * k;
        ctx.strokeStyle = toned('#31A99E', s);
        ctx.lineWidth = lw(s, 0.07);
        ctx.beginPath();
        ctx.arc(cx, cy, R * 0.95, 0, TAU);
        ctx.stroke();
      }
    },
  },
  {
    id: 'lattice',
    name: 'Lattice',
    ext: 1.4,
    draw(ctx, s) {
      const { cx, cy, R, t, k } = s;
      const r = R * 0.5;
      const d = Math.sqrt(3) * r;
      const spin = (t * TAU) / 24;
      const originals = [
        '#FFBD53',
        '#C8DF69',
        '#99CF5C',
        '#5DB779',
        '#31A99E',
        '#23B5C7',
        '#3D67DE',
      ];
      const base = [-Math.PI / 3, 0, Math.PI / 3, (2 * Math.PI) / 3, Math.PI, (-2 * Math.PI) / 3];
      // The bloom: petals open one after another, stay open, and close again in reverse.
      const T = 4.6;
      const p = t % T;
      const bloom = (i: number) => {
        let b: number;
        if (p < 1.6) b = easeOut(clamp((p - i * 0.1) / 1.0));
        else if (p < 3.5) b = 1;
        else b = 1 - easeIO(clamp((p - 3.5 - (5 - i) * 0.06) / 0.8));
        return lerp(b, 1, k);
      };
      // The light wanders: an orbit whose radius and speed breathe, so no two passes are alike.
      const la = (t * TAU) / 3.4 + 0.6 * Math.sin(t * 1.3);
      const lr = d * (0.75 + 0.35 * Math.sin(t * 0.9));
      const L: PointType = [cx + lr * Math.cos(la), cy + lr * Math.sin(la)];
      const glow = (x: number, y: number, i: number, b: number) => {
        const dist = Math.hypot(x - L[0], y - L[1]);
        let a = pow(clamp(1 - dist / (d * 1.55)), 1.6);
        a = 0.18 + 0.82 * a + 0.06 * Math.sin(t * 2.1 + i * 1.7);
        return lerp(lerp(0.45, clamp(a), b), 1, k);
      };
      const cell = (x: number, y: number, i: number, a: number, sc: number, rot: number) => {
        ctx.globalAlpha = s.alpha * lerp(0.25, 1, a);
        poly(ctx, hex(x, y, r * 0.86 * sc, rot));
        // A hexagon's narrowest width is across its flats: √3 times its radius.
        fillMark(ctx, s, originals[i], Math.sqrt(3) * r * 0.86 * sc);
      };
      const opened = base.reduce((sum, _, j) => sum + bloom(j), 0) / 6;
      ctx.globalAlpha = s.alpha * opened;
      ctx.lineWidth = lw(s, 0.03);
      ctx.lineJoin = 'round';
      ctx.strokeStyle = ink(s, 0.08);
      base.forEach((a0) => {
        const a = a0 + spin;
        poly(ctx, hex(cx + d * Math.cos(a), cy + d * Math.sin(a), r * 0.86, -Math.PI / 2 + spin));
        ctx.stroke();
      });
      // Petals first, so a closed bud hides them behind the centre.
      base.forEach((a0, j) => {
        const i = j + 1;
        const b = bloom(j);
        // Opening unwinds towards the turn and closing winds on with it, so the flow never reverses.
        // A closed petal sits behind the bud, so the switch between the two is never seen.
        const twist = (1 - b) * 1.15 * (p >= 3.5 && k === 0 ? 1 : -1);
        const ang = a0 + spin + twist;
        const x0 = cx + d * b * Math.cos(ang);
        const y0 = cy + d * b * Math.sin(ang);
        const a = glow(x0, y0, i, b);
        const dist = Math.hypot(x0 - L[0], y0 - L[1]);
        const pull = R * 0.07 * a * b * (1 - k);
        const x = dist ? x0 + ((L[0] - x0) / dist) * pull : x0;
        const y = dist ? y0 + ((L[1] - y0) / dist) * pull : y0;
        const sc = lerp(0.35, lerp(0.6, 1, a), b);
        cell(
          x,
          y,
          i,
          a * lerp(0.6, 1, b),
          sc,
          -Math.PI / 2 + spin + twist * 1.4 + 0.12 * Math.sin(t * 1.1 + i) * b * (1 - k),
        );
      });
      const open = base.reduce((sum, _, j) => sum + bloom(j), 0) / 6;
      const ca = glow(cx, cy, 0, open);
      const budSc = lerp(1.08, lerp(0.6, 1, ca), open);
      // The bud hides the folded petals behind it. The prototype masks them in the launch colour;
      // here the mask cuts the canvas clear, so the bud sits right on any ground (a card, a button).
      ctx.globalAlpha = s.alpha;
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = '#000000';
      poly(ctx, hex(cx, cy, r * 0.86 * budSc, -Math.PI / 2 + spin));
      ctx.fill();
      ctx.globalCompositeOperation = 'source-over';
      cell(cx, cy, 0, Math.max(ca, 1 - open), budSc, -Math.PI / 2 + spin);
    },
  },
  {
    id: 'strands',
    name: 'Three strands',
    ext: 1.3,
    draw(ctx, s) {
      const { cx, cy, R, t, k } = s;
      const T = 4.8;
      const tau = t / T;
      const ph = tau - Math.floor(tau);
      const warped = Math.floor(tau) + ph - 0.06 * Math.sin(TAU * ph);
      const radii = [0.5, 0.85, 1.2];
      const turns = [3, 2, 1];
      const lens = [1.7, 1.3, 1.0];
      const cols = trio(s);
      ctx.lineCap = 'round';
      radii.forEach((rr, j) => {
        const r = R * rr;
        ctx.globalAlpha = s.alpha;
        ctx.lineWidth = lw(s, 0.1);
        ctx.strokeStyle = ink(s, 0.06);
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, TAU);
        ctx.stroke();
        let th = -Math.PI / 2 + TAU * turns[j] * warped;
        const dlt = (((-Math.PI / 2 - th) % TAU) + TAU) % TAU;
        th += dlt * k;
        const L = lerp(lens[j], TAU * 0.999, k * k);
        ctx.strokeStyle = cols[j];
        ctx.beginPath();
        ctx.arc(cx, cy, r, th - L / 2, th + L / 2);
        ctx.stroke();
      });
    },
  },
  {
    id: 'counterpoint',
    name: 'Counterpoint',
    ext: 1.05,
    draw(ctx, s) {
      const { cx, cy, R, t, k } = s;
      const T = 3.6;
      const ph = (t % T) / T;
      const base = (t * TAU) / 14 - Math.PI / 2;
      const off = (Math.PI / 6) * Math.sin(Math.PI * easeIO(ph)) * (1 - k);
      ctx.lineJoin = 'round';
      ctx.lineWidth = lw(s, 0.075);
      const pairs: [number, string[]][] = [
        [base + off, tones(WAITING_PALETTE.frame, s)],
        [base - off, tones(WAITING_PALETTE.ring.slice(1), s)],
      ];
      pairs.forEach(([rot, pal]) => {
        const g = ctx.createLinearGradient(cx - R, cy - R, cx + R, cy + R);
        pal.forEach((c, i) => g.addColorStop(i / (pal.length - 1), c));
        ctx.globalAlpha = s.alpha * 0.92;
        ctx.strokeStyle = g;
        poly(ctx, hex(cx, cy, R, rot));
        ctx.stroke();
      });
    },
  },
  {
    id: 'gathering',
    name: 'Gathering',
    ext: 1.2,
    draw(ctx, s) {
      const { cx, cy, R, t, k } = s;
      const V = hex(cx, cy, R * 1.05);
      ctx.globalAlpha = s.alpha;
      ctx.lineJoin = 'round';
      ctx.lineWidth = lw(s, 0.06);
      ctx.strokeStyle = ink(s, lerp(0.1, 0.22, k));
      poly(ctx, V);
      ctx.stroke();
      const T = 2.8;
      const n = Math.floor(t / T);
      const ph = (t % T) / T;
      let g: number;
      let P = n;
      if (ph < 0.4) g = 1 - easeIO(ph / 0.4);
      else if (ph < 0.55) g = 0;
      else {
        const e = easeIO((ph - 0.55) / 0.45);
        g = e;
        P = n + e;
      }
      g *= 1 - k;
      const originals = trioOriginals(s);
      const radius = Math.max(R * 0.14, s.minW * 1.2);
      [-2, 2, 0].forEach((o, j) => {
        const [x, y] = onHex(V, P + o * g);
        ctx.globalAlpha = s.alpha;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, TAU);
        fillMark(ctx, s, originals[j], radius * 2);
      });
    },
  },
  {
    id: 'assemble',
    name: 'Assemble',
    ext: 2.05,
    draw(ctx, s) {
      const { cx, cy, R, t, k } = s;
      const V = hex(cx, cy, R);
      const cols = tones(['#99CF5C', '#5DB779', '#31A99E', '#23B5C7', '#288EE0', '#3D67DE'], s);
      const T = 3.2;
      const p = t % T;
      ctx.lineCap = 'round';
      ctx.lineWidth = lw(s, 0.09);
      for (let i = 0; i < 6; i++) {
        const a = V[i];
        const b = V[(i + 1) % 6];
        const mx = (a[0] + b[0]) / 2;
        const my = (a[1] + b[1]) / 2;
        const nx = (mx - cx) / Math.hypot(mx - cx, my - cy);
        const ny = (my - cy) / Math.hypot(mx - cx, my - cy);
        const tin = clamp((p - i * 0.16) / 0.45);
        const tout = clamp((p - 1.95 - i * 0.12) / 0.42);
        let away: number;
        let turn: number;
        let vis: number;
        if (tout > 0) {
          const e = easeIO(tout);
          away = e;
          turn = e * 0.9;
          vis = 1 - e;
        } else {
          const e = easeOut(tin);
          away = 1 - e;
          turn = -(1 - e) * 0.9;
          vis = e;
        }
        away *= 1 - k;
        turn *= 1 - k;
        vis = Math.max(vis, k);
        if (vis <= 0) continue;
        const ox = nx * R * 0.75 * away;
        const oy = ny * R * 0.75 * away;
        const cs = Math.cos(turn);
        const sn = Math.sin(turn);
        const rot = ([x, y]: PointType): PointType => [
          mx + ox + (x - mx) * cs - (y - my) * sn,
          my + oy + (x - mx) * sn + (y - my) * cs,
        ];
        const [x1, y1] = rot(a);
        const [x2, y2] = rot(b);
        ctx.globalAlpha = s.alpha * vis;
        ctx.strokeStyle = cols[i];
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }
    },
  },
  {
    id: 'connect',
    name: 'Connect',
    ext: 1.2,
    draw(ctx, s) {
      const { cx, cy, R, t, k } = s;
      const V = hex(cx, cy, R * 1.05);
      const C: PointType = [cx, cy];
      const edges: [PointType, PointType][] = [];
      for (let i = 0; i < 6; i++) {
        edges.push([C, V[i]]);
        edges.push([V[i], V[(i + 1) % 6]]);
      }
      const T = 3.1;
      const p = t % T;
      const fadeOut = p > 2.45 ? 1 - easeIO(clamp((p - 2.45) / 0.5)) : 1;
      const linkTones = tones(
        [...WAITING_PALETTE.ring, WAITING_PALETTE.frame[1], WAITING_PALETTE.frame[3]],
        s,
      );
      ctx.lineCap = 'round';
      ctx.lineWidth = lw(s, 0.065);
      const seen = new Set<PointType>();
      edges.forEach(([a, b], i) => {
        let g = easeOut(clamp((p - i * 0.14) / 0.2));
        g = Math.max(g * fadeOut, k);
        if (g <= 0) return;
        seen.add(a);
        seen.add(b);
        ctx.globalAlpha = s.alpha * g;
        ctx.strokeStyle = along(linkTones, i / 11);
        ctx.beginPath();
        ctx.moveTo(a[0], a[1]);
        ctx.lineTo(lerp(a[0], b[0], g), lerp(a[1], b[1], g));
        ctx.stroke();
      });
      [C, ...V].forEach((q) => {
        ctx.globalAlpha = s.alpha * (seen.has(q) ? 1 : 0.3) * Math.max(fadeOut, k, 0.3);
        ctx.fillStyle = ink(s, 0.8);
        ctx.beginPath();
        ctx.arc(q[0], q[1], Math.max(R * 0.085, s.minW), 0, TAU);
        ctx.fill();
      });
    },
  },
  {
    id: 'align',
    name: 'Align',
    ext: 1.4,
    draw(ctx, s) {
      const { cx, cy, R, t, k } = s;
      const T = 2.6;
      const n = Math.floor(t / T);
      const ph = (t % T) / T;
      const d = n % 2 ? -1 : 1;
      let g: number;
      let dir = d;
      if (ph < 0.4) g = 1 - easeIO(ph / 0.4);
      else if (ph < 0.6) g = 0;
      else {
        g = easeIO((ph - 0.6) / 0.4);
        dir = -d;
      }
      g *= 1 - k;
      const originals = trioOriginals(s);
      const h = Math.max(R * 0.22, s.minW * 1.6);
      const w = R * 1.5;
      const gap = R * 0.44;
      [-1, 0, 1].forEach((row, j) => {
        const x = cx + [1, -1, 1][j] * dir * R * 0.6 * g;
        const y = cy + row * gap;
        ctx.globalAlpha = s.alpha * (1 - 0.35 * g);
        ctx.beginPath();
        ctx.roundRect(x - w / 2, y - h / 2, w, h, h / 2);
        fillMark(ctx, s, originals[j], h);
      });
    },
  },
]);

export function waitingStudy(id: WaitingStudyId): IWaitingStudy {
  return WAITING_STUDIES.find((study) => study.id === id) ?? WAITING_STUDIES[0];
}

export function isWaitingStudyId(value: unknown): value is WaitingStudyId {
  return typeof value === 'string' && (WAITING_STUDY_IDS as readonly string[]).includes(value);
}

/**
 * WAIT-3 and WAIT-5: the study a launch draws. A fixed choice (the person's setting) wins. Otherwise
 * one of the ten, uniformly from the nine that were not last launch's pick; with no last pick, any of
 * the ten. `random` returns a number in [0, 1), like `Math.random`.
 */
export function pickWaitingStudy(input: {
  last?: string | null;
  fixed?: string | null;
  random?: () => number;
}): WaitingStudyId {
  if (isWaitingStudyId(input.fixed)) return input.fixed;
  const random = input.random ?? Math.random;
  const pool = WAITING_STUDY_IDS.filter((id) => id !== input.last);
  return pool[Math.min(pool.length - 1, Math.floor(random() * pool.length))];
}

/**
 * WAIT-6: the unit `R` for a study in a square box of `boxPx`, so its reach fits the box. The
 * reach is `ext` units of `R`, plus up to one minimum stroke where a line or dot is held at its
 * minimum width rather than scaled with `R`.
 */
export function waitingSmallRadius(study: IWaitingStudy, boxPx: number, minW: number): number {
  return (boxPx / 2 - minW) / study.ext;
}

/** WAIT-6: the launch unit `R` for a window whose short side is `shortSidePx`. */
export function waitingLaunchRadius(shortSidePx: number): number {
  return Math.min(shortSidePx * WAITING_MOTION.launchUnit, WAITING_MOTION.launchUnitMaxPx);
}

/** WAIT-10: the Reduce Motion opacity at `t` seconds. */
export function waitingReducedAlpha(t: number): number {
  const { minAlpha, maxAlpha, periodS } = WAITING_MOTION.reduceMotion;
  return minAlpha + (maxAlpha - minAlpha) * (0.5 + 0.5 * Math.cos((t * TAU) / periodS));
}

/** WAIT-5: the *Waiting animation* setting's default, a new study each launch. */
export const WAITING_CHOICE_RANDOM = 'random';

/** WAIT-5: the setting's value: Random, or one study for every launch. */
export type WaitingChoiceType = typeof WAITING_CHOICE_RANDOM | WaitingStudyId;

export function isWaitingChoice(value: unknown): value is WaitingChoiceType {
  return value === WAITING_CHOICE_RANDOM || isWaitingStudyId(value);
}

/** WAIT-5: the person's setting as the server keeps it (`users.preferences.waitingAnimation`). */
export interface IWaitingAnimationPreferences {
  study: WaitingChoiceType;
}

export const DEFAULT_WAITING_ANIMATION_PREFERENCES: IWaitingAnimationPreferences = {
  study: WAITING_CHOICE_RANDOM,
};
